// frontend/src/utils/stages.js

// Canonical backend enum keys
export const STAGE_ALIASES = Object.freeze({
  IDEATION: "Idea Pitch",
  FINALIZED_TOPICS: "Topic Review",
  FIRST_DRAFT: "First Draft",
  STRUCTURAL_EDIT: "Structural Review",
  SECOND_DRAFT: "Revised Draft",
  COPY_EDIT: "Copy Edit",
  THIRD_DRAFT: "Polished Draft",
  FACT_CHECK: "Fact Check",
  FINAL_DRAFT: "Final Draft",
  PROOFREAD: "Proofread",
  REVIEW: "Final Review",
  SCHEDULE_PUBLISH: "Schedule/Publish",
});

// Explicit order (so Object iteration order can’t break)
export const STAGE_ORDER = Object.freeze([
  "IDEATION",
  "FINALIZED_TOPICS",
  "FIRST_DRAFT",
  "STRUCTURAL_EDIT",
  "SECOND_DRAFT",
  "COPY_EDIT",
  "THIRD_DRAFT",
  "FACT_CHECK",
  "FINAL_DRAFT",
  "PROOFREAD",
  "REVIEW",
  "SCHEDULE_PUBLISH",
]);

// Color palette per stage key (keyed by enum)
export const STAGE_COLORS = Object.freeze({
  IDEATION: "#FFEA00",
  FINALIZED_TOPICS: "#FFC400",
  FIRST_DRAFT: "#FFA500",
  STRUCTURAL_EDIT: "#FF9100",
  SECOND_DRAFT: "#FF3D00",
  COPY_EDIT: "#FF0080",
  THIRD_DRAFT: "#D500F9",
  FACT_CHECK: "#651FFF",
  FINAL_DRAFT: "#2979FF",
  PROOFREAD: "#00E5FF",
  REVIEW: "#00E676",
  SCHEDULE_PUBLISH: "#76FF03",
});

// ============================
// Helper Functions
// ============================

// Get human-readable label
export const toHuman = (key) => STAGE_ALIASES[key] || key;

// Get enum key from label
export const toKey = (label) =>
  Object.entries(STAGE_ALIASES).find(([, v]) => v === label)?.[0] || "IDEATION";

// Get color for a given stage key
export const stageColor = (key) => STAGE_COLORS[key] || "#ccc";

// Get next/previous stage (safe navigation)
export const nextStage = (key) => {
  const i = STAGE_ORDER.indexOf(key);
  return i >= 0 && i < STAGE_ORDER.length - 1 ? STAGE_ORDER[i + 1] : key;
};

export const prevStage = (key) => {
  const i = STAGE_ORDER.indexOf(key);
  return i > 0 ? STAGE_ORDER[i - 1] : key;
};

// Generate section data (used by Editorial Dashboard)
export const buildSections = () =>
  STAGE_ORDER.map((key) => ({
  key,
  title: STAGE_ALIASES[key],
  color: STAGE_COLORS[key] || "#ccc",
}));

// Action labels for writer/editor submissions
export const STAGE_SUBMIT_LABELS = Object.freeze({
  IDEATION: "Submit Topic Pitch",
  FINALIZED_TOPICS: "Submit Topic Review",
  FIRST_DRAFT: "Submit 1st Draft",
  STRUCTURAL_EDIT: "Submit Structural Review",
  SECOND_DRAFT: "Submit Revised Draft",
  COPY_EDIT: "Submit Copy Edit",
  THIRD_DRAFT: "Submit Polished Draft",
  FACT_CHECK: "Submit Fact Check",
  FINAL_DRAFT: "Submit Final Draft",
  PROOFREAD: "Submit for Final Review",
  REVIEW: "Submit for Publishing",
  SCHEDULE_PUBLISH: "Schedule/Publish",
});

// Which stages are “writer-owned” vs “editor-owned”
export const WRITER_STAGES = new Set([
  "IDEATION",
  "FIRST_DRAFT",
  "SECOND_DRAFT",
  "THIRD_DRAFT",
  "FINAL_DRAFT",
]);

export const EDITOR_STAGES = new Set([
  "FINALIZED_TOPICS",
  "STRUCTURAL_EDIT",
  "COPY_EDIT",
  "FACT_CHECK",
  "PROOFREAD",
]);

export const LEAD_EDITOR_STAGES = new Set([
  "REVIEW",
  "SCHEDULE_PUBLISH",
]);
